/**
 * FollowButton — shared follow/unfollow toggle (DEV-1331, epic DEV-1329).
 *
 * Follow → Following, with the followed state revealing "Unfollow" on hover
 * (guards against accidental unfollows). Shows the live follower count.
 * Polymorphic: works for any followable target (chronicle, project, community,
 * user, ...) via the generic /api/follows endpoints.
 */

import { useEffect, useState, useCallback } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import {
  followEntity,
  unfollowEntity,
  getFollowStatus,
  type FollowTargetType,
} from '../../api/follows';
import styles from './FollowButton.module.css';

interface Props {
  targetType: FollowTargetType;
  targetGuid: string;
  /** Show the follower count beside the button. Default true. */
  showCount?: boolean;
  /** Compact variant for dense surfaces (cards). */
  compact?: boolean;
  /**
   * DEV-1447 — seed the followed state from a caller that already knows it,
   * skipping this instance's own GET /api/follows/status round-trip.
   *
   * Card grids (the Discover catalogs) render dozens of these at once; letting
   * each one self-fetch would fire N requests on mount. The catalog instead
   * reads the user's full followed set once via `useApiMyFollows` and seeds
   * every card from it. Omit the prop entirely and the button self-fetches as
   * before — the single-entity surfaces (project/chronicle/community detail)
   * are unchanged.
   */
  initialFollowing?: boolean;
}

export default function FollowButton({
  targetType,
  targetGuid,
  showCount = true,
  compact = false,
  initialFollowing,
}: Props) {
  const { t } = useAppTranslation('common');
  const seeded = initialFollowing !== undefined;
  const [following, setFollowing] = useState(initialFollowing ?? false);
  const [count, setCount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(seeded);

  useEffect(() => {
    // Seeded by the caller — no per-instance status fetch. The follower count
    // stays null, which `showCount` already tolerates (renders nothing).
    if (seeded) {
      setFollowing(initialFollowing as boolean);
      setLoaded(true);
      return;
    }
    let cancelled = false;
    getFollowStatus(targetType, targetGuid)
      .then((s) => {
        if (cancelled) return;
        setFollowing(s.following);
        setCount(s.followerCount);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [targetType, targetGuid, seeded, initialFollowing]);

  const toggle = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    const wasFollowing = following;
    const prevCount = count ?? 0;
    // Optimistic update
    setFollowing(!wasFollowing);
    setCount(prevCount + (wasFollowing ? -1 : 1));
    try {
      const res = wasFollowing
        ? await unfollowEntity(targetType, targetGuid)
        : await followEntity(targetType, targetGuid);
      setFollowing(res.following);
      setCount(res.followerCount);
    } catch {
      // Revert on failure
      setFollowing(wasFollowing);
      setCount(prevCount);
    } finally {
      setBusy(false);
    }
  }, [busy, following, count, targetType, targetGuid]);

  if (!loaded) return null; // avoid a flash of the wrong state

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={`${styles.btn} ${following ? styles.following : styles.notFollowing} ${compact ? styles.compact : ''}`}
        onClick={toggle}
        disabled={busy}
        aria-pressed={following}
      >
        {following ? (
          <>
            <span className={styles.followingLabel}>{t('follow.following')}</span>
            <span className={styles.unfollowLabel}>{t('follow.unfollow')}</span>
          </>
        ) : (
          <span>{t('follow.follow')}</span>
        )}
      </button>
      {showCount && count !== null && count > 0 && (
        <span className={styles.count}>{count.toLocaleString()}</span>
      )}
    </div>
  );
}
